import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import CollapsibleText from "@diligentcorp/atlas-react-collapsible-text";
import DownloadIndicator from "./DownloadIndicator";
import ArrowLeftIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/ArrowLeft.js";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DiligentLogo from "./DiligentLogo";
import SearchFilter from "./SearchFilter";
import type { AdvancedSearchParams } from "../types/search";
import FilterPanel from "./FilterPanel";
import { searchService, type SearchResult } from "../services/searchService";
import { summaryService } from "../services/summaryService";
import {
  filterValuesService,
  type FilterValuesResponse,
} from "../services/filterValuesService";
import {
  downloadPolicyDocumentComplete,
  type PolicyDownloadRequest,
} from "../services/policyDownloadService";

interface SearchResultsProps {
  searchQuery: string; // Search identifier used to trigger performSearch
  onBack: () => void;
  currentSearchText?: string; // Current value from the search input
  advancedSearchParams?: {
    policyCode?: string;
    documentType?: string;
    documentCategory?: string;
    documentSubCategory?: string;
    policyLanguage?: string;
    publishedSince?: Date | null;
  };
  filterDrawerOpen?: boolean;
  onCloseFilterDrawer?: () => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  searchQuery,
  onBack,
  currentSearchText,
  advancedSearchParams,
  filterDrawerOpen,
  onCloseFilterDrawer,
}) => {
  const [assistantResponse, setAssistantResponse] = useState<string>("");
  const [isLoadingResponse, setIsLoadingResponse] = useState(true);
  const [assistantResponseError, setAssistantResponseError] =
    useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isLoadingResults, setIsLoadingResults] = useState(true);
  const [searchResultsError, setSearchResultsError] = useState<boolean>(false);
  const [hideAssistantResponse, setHideAssistantResponse] = useState<boolean>(false);
  const [_filterValues, setFilterValues] =
    useState<FilterValuesResponse | null>(null);
  const [_isLoadingFilterValues, setIsLoadingFilterValues] = useState(true);
  const [_filterValuesError, setFilterValuesError] = useState<boolean>(false);
  // Drawer is controlled by parent via props on small/medium screens

  // Active filter selections
  const [activeFilters, setActiveFilters] = useState<{
    sortBy: Record<string, boolean>;
    language: Record<string, boolean>;
    documentType: Record<string, boolean>;
    documentCategory: Record<string, boolean>;
    documentSubCategory: Record<string, boolean>;
    policyCode: Record<string, boolean>;
    // publishedSince is a single date, not a map of booleans
  }>({
    sortBy: {},
    language: {},
    documentType: {},
    documentCategory: {},
    documentSubCategory: {},
    policyCode: {},
  });

  // Separate state for publishedSince since it is a single Date value
  const [publishedSince, setPublishedSince] = useState<Date | null>(null);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  
  // Helper to extract error message and optional code from unknown errors
  const getErrorInfo = (error: unknown): { code?: string; message: string } => {
    if (error instanceof Error) {
      const withCode = error as Error & { code?: string };
      return { code: withCode.code, message: withCode.message };
    }
    if (typeof error === "string") {
      return { message: error };
    }
    try {
      return { message: JSON.stringify(error) };
    } catch {
      return { message: String(error) };
    }
  };

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));

  // Function to load filter values on initial page load
  const loadFilterValues = useCallback(async () => {
    try {
      setIsLoadingFilterValues(true);
      setFilterValuesError(false);

      console.log("🔍 Loading filter values...");
      const filterValuesResult = await filterValuesService.getFilterValues();

      console.log("🎯 Filter Values Response:", filterValuesResult);
      setFilterValues(filterValuesResult);

      // Extract options for easy access (optional - for debugging/logging)
      if (filterValuesResult) {
        const extractedOptions =
          filterValuesService.extractFilterOptions(filterValuesResult);
        console.log("📊 Extracted Filter Options:", extractedOptions);
      }
    } catch (error) {
      console.error("❌ Error loading filter values:", error);
      setFilterValuesError(true);
    } finally {
      setIsLoadingFilterValues(false);
    }
  }, []);

  // Function to convert active filters to search parameters
  const createSearchParamsFromFilters = useCallback(
    (searchText: string) => {
      const getAllSelectedFilters = (
        filters: Record<string, boolean>
      ): string | undefined => {
        const selected = Object.keys(filters).filter((key) => filters[key]);
        return selected.length > 0 ? selected.join(",") : undefined;
      };

      // Create search parameters combining text and filters
      const searchParams: {
        policyCode?: string;
        documentType?: string;
        documentCategory?: string;
        documentSubCategory?: string;
        policyLanguage?: string;
        publishedSince?: Date | null;
      } & { searchText?: string } = {};

      // Add search text if provided
      if (searchText && searchText.trim()) {
        searchParams.searchText = searchText.trim();
      }

      // Convert filter selections to search parameters (comma-separated for multiple values)
      const selectedDocumentTypes = getAllSelectedFilters(
        activeFilters.documentType
      );
      if (selectedDocumentTypes) {
        searchParams.documentType = selectedDocumentTypes;
      }

      const selectedDocumentCategories = getAllSelectedFilters(
        activeFilters.documentCategory
      );
      if (selectedDocumentCategories) {
        searchParams.documentCategory = selectedDocumentCategories;
      }

      const selectedDocumentSubCategories = getAllSelectedFilters(
        activeFilters.documentSubCategory
      );
      if (selectedDocumentSubCategories) {
        searchParams.documentSubCategory = selectedDocumentSubCategories;
      }

      const selectedLanguages = getAllSelectedFilters(activeFilters.language);
      if (selectedLanguages) {
        searchParams.policyLanguage = selectedLanguages;
      }

      const selectedPolicyCodes = getAllSelectedFilters(
        activeFilters.policyCode
      );
      if (selectedPolicyCodes) {
        searchParams.policyCode = selectedPolicyCodes;
      }

      if (publishedSince) {
        searchParams.publishedSince = publishedSince;
      }

      return searchParams;
    },
    [activeFilters, publishedSince]
  );

  // Function to perform search using both APIs with independent error handling
  const performSearch = useCallback(
    async (query: string, useFilters: boolean = false) => {
      try {
        setIsLoadingResponse(true);
        setIsLoadingResults(true);
        setAssistantResponseError(false); // Reset error state
        setSearchResultsError(false); // Reset error state

        // Determine search type and parameters
        let searchPromise;

        if (
          useFilters ||
          publishedSince !== null ||
          Object.values(activeFilters).some((filterGroup) =>
            Object.values(filterGroup).some((value) => value)
          )
        ) {
          // Use combined text + filter search
          const combinedParams = createSearchParamsFromFilters(query);
          console.log("🔍 Combined Search Parameters:", combinedParams);
          searchPromise = searchService.searchDocumentsAdvanced(combinedParams);
        } else if (advancedSearchParams) {
          // Use original advanced search parameters
          console.log(
            "📊 Advanced Search Parameters for API:",
            advancedSearchParams
          );
          searchPromise =
            searchService.searchDocumentsAdvanced(advancedSearchParams);
        } else {
          // Use regular text search
          console.log("📝 Regular Search Query:", query);
          searchPromise = searchService.searchDocuments(query);
        }

        // Build params for summary based on same filters
        const summaryParams = (() => {
          if (
            useFilters ||
            publishedSince !== null ||
            Object.values(activeFilters).some((filterGroup) =>
              Object.values(filterGroup).some((value) => value)
            )
          ) {
            const combinedParams = createSearchParamsFromFilters(query);
            return combinedParams;
          } else if (advancedSearchParams) {
            return advancedSearchParams;
          }
          return { searchText: query };
        })();

        // Kick off summary request without blocking UI; update when it completes
        summaryService
          .getSummary(summaryParams)
          .then((res) => {
            console.log("Assistant Response:", res);
            setAssistantResponse(res.answer);
            setHideAssistantResponse(false);
          })
          .catch((err: unknown) => {
            const { code, message } = getErrorInfo(err);
            console.error("Error getting assistant response:", message);
            if (code === "Forbidden" || message.includes("Forbidden")) {
              setHideAssistantResponse(true);
            } else {
              setAssistantResponse(
                "Sorry, I couldn't get an AI response at this time. Please try again later."
              );
              setAssistantResponseError(true);
            }
          })
          .finally(() => {
            setIsLoadingResponse(false);
          });

        // Await search results and render as soon as they are ready
        try {
          const searchRes = await searchPromise;
          console.log("Search Response:", searchRes);
          setSearchResults(searchRes.data.documents);
          setIsLoadingResults(false);
        } catch (err) {
          console.error("Error searching documents:", err);
          setSearchResults([]);
          setSearchResultsError(true);
          setIsLoadingResults(false);
        }
      } catch (error) {
        console.error("Unexpected error during search setup:", error);
        // Only affect search results here; summary handler continues independently
        setSearchResults([]);
        setSearchResultsError(true);
        setIsLoadingResults(false);
      }
    },
    [advancedSearchParams, activeFilters, publishedSince]
  );

  // Track latest search text without causing effect re-runs on each keystroke
  const latestSearchTextRef = useRef<string>("");
  useEffect(() => {
    latestSearchTextRef.current =
      typeof currentSearchText === "string" ? currentSearchText : "";
  }, [currentSearchText]);

  // Function to trigger search when filters change (reads latest text from ref)
  const performFilteredSearch = useCallback(() => {
    const text = latestSearchTextRef.current?.trim?.() || "";
    performSearch(text, true);
  }, [performSearch]);

  // Responsive font size function to match "Policy Portal" title
  const getMainTitleFontSize = () => {
    if (isXs) return "1.5rem"; // XS screen (24px)
    if (isSm) return "1.625rem"; // SM screen (26px)
    if (isMd) return "1.75rem"; // MD screen (28px)
    return "1.875rem"; // LG+ screen (30px)
  };

  // Load filter values on component mount
  useEffect(() => {
    loadFilterValues();
  }, [loadFilterValues]);

  // Trigger search when component mounts or search identifier changes
  // Note: depend only on searchQuery to avoid re-running when filters change,
  // which would otherwise trigger an extra unfiltered request.
  useEffect(() => {
    if (searchQuery) {
      performSearch(searchQuery);
    }
  }, [searchQuery]);

  // Filter change handlers for FilterPanel component

  const handleLanguageChange = useCallback(
    (filters: Record<string, boolean>) => {
      console.log("Language filters changed:", filters);
      setActiveFilters((prev) => ({ ...prev, language: filters }));
    },
    []
  );

  const handleDocumentTypeChange = useCallback(
    (filters: Record<string, boolean>) => {
      console.log("Document type filters changed:", filters);
      setActiveFilters((prev) => ({ ...prev, documentType: filters }));
    },
    []
  );

  const handleDocumentCategoryChange = useCallback(
    (filters: Record<string, boolean>) => {
      console.log("Document category filters changed:", filters);
      setActiveFilters((prev) => ({ ...prev, documentCategory: filters }));
    },
    []
  );

  const handleDocumentSubCategoryChange = useCallback(
    (filters: Record<string, boolean>) => {
      console.log("Document subcategory filters changed:", filters);
      setActiveFilters((prev) => ({ ...prev, documentSubCategory: filters }));
    },
    []
  );

  const handlePolicyCodeChange = useCallback(
    (filters: Record<string, boolean>) => {
      console.log("Policy code filters changed:", filters);
      setActiveFilters((prev) => ({ ...prev, policyCode: filters }));
    },
    []
  );

  const handlePublishedSinceChange = useCallback((date: Date | null) => {
    console.log("Published since changed:", date);
    setPublishedSince(date);
  }, []);

  const hasAnyActiveFilter = React.useMemo(() => {
    const groups = [
      activeFilters.language,
      activeFilters.documentType,
      activeFilters.documentCategory,
      activeFilters.documentSubCategory,
      activeFilters.policyCode,
    ];
    const anyGroupSelected = groups.some((group) =>
      Object.values(group).some(Boolean)
    );
    return anyGroupSelected || publishedSince !== null;
  }, [activeFilters, publishedSince]);

  // Debounced live search on input change (optional live UX)
  const requestSeqRef = useRef<number>(0);
  const searchAbortRef = useRef<AbortController | null>(null);
  const summaryAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const text =
      typeof currentSearchText === "string" ? currentSearchText.trim() : "";

    // Skip if not enough characters or same as last submitted searchQuery
    if (text.length < 2) return;
    if (typeof searchQuery === "string" && text === searchQuery.trim()) return;

    const DEBOUNCE_MS = 400;
    const timeoutId = setTimeout(async () => {
      const reqId = ++requestSeqRef.current;

      try {
        // Cancel any in-flight requests
        if (searchAbortRef.current) {
          searchAbortRef.current.abort();
        }
        if (summaryAbortRef.current) {
          summaryAbortRef.current.abort();
        }
        searchAbortRef.current = new AbortController();
        summaryAbortRef.current = new AbortController();

        setIsLoadingResults(true);
        setSearchResultsError(false);
        setIsLoadingResponse(true);
        setAssistantResponseError(false);

        const useFilters = hasAnyActiveFilter || publishedSince !== null;

        if (useFilters) {
          const combinedParams = createSearchParamsFromFilters(text);
          // Summary (non-blocking)
          summaryService
            .getSummary(combinedParams, {
              signal: summaryAbortRef.current.signal,
            })
            .then((res) => {
              if (reqId === requestSeqRef.current) {
                setAssistantResponse(res.answer);
                setHideAssistantResponse(false);
              }
            })
            .catch((err: unknown) => {
              if (err instanceof DOMException && err.name === "AbortError") {
                // Keep loading state; a newer request is in-flight
                return;
              }
              if (reqId === requestSeqRef.current) {
                const { code, message } = getErrorInfo(err);
                console.error(
                  "Error getting assistant response (debounced):",
                  message
                );
                if (code === "Forbidden" || message.includes("Forbidden")) {
                  setHideAssistantResponse(true);
                } else {
                  setAssistantResponse(
                    "Sorry, I couldn't get an AI response at this time. Please try again later."
                  );
                  setAssistantResponseError(true);
                }
              }
            })
            .finally(() => {
              if (reqId === requestSeqRef.current) {
                setIsLoadingResponse(false);
              }
            });

          try {
            const searchRes = await searchService.searchDocumentsAdvanced(
              combinedParams,
              { signal: searchAbortRef.current.signal }
            );
            if (reqId === requestSeqRef.current) {
              setSearchResults(searchRes.data.documents);
              setIsLoadingResults(false);
            }
          } catch (err) {
            if (err instanceof DOMException && err.name === "AbortError") {
              // Keep loading state; a newer request is in-flight
              return;
            }
            if (reqId === requestSeqRef.current) {
              console.error(
                "Error searching documents (debounced, filters):",
                err
              );
              setSearchResults([]);
              setSearchResultsError(true);
              setIsLoadingResults(false);
            }
          }
        } else {
          // No active filters: plain text search + summary
          summaryService
            .getSummary(
              { searchText: text },
              { signal: summaryAbortRef.current.signal }
            )
            .then((res) => {
              if (reqId === requestSeqRef.current) {
                setAssistantResponse(res.answer);
                setHideAssistantResponse(false);
              }
            })
            .catch((err: unknown) => {
              if (err instanceof DOMException && err.name === "AbortError") {
                // Keep loading state; a newer request is in-flight
                return;
              }
              if (reqId === requestSeqRef.current) {
                const { code, message } = getErrorInfo(err);
                console.error(
                  "Error getting assistant response (debounced):",
                  message
                );
                if (code === "Forbidden" || message.includes("Forbidden")) {
                  setHideAssistantResponse(true);
                } else {
                  setAssistantResponse(
                    "Sorry, I couldn't get an AI response at this time. Please try again later."
                  );
                  setAssistantResponseError(true);
                }
              }
            })
            .finally(() => {
              if (reqId === requestSeqRef.current) {
                setIsLoadingResponse(false);
              }
            });

          try {
            const searchRes = await searchService.searchDocuments(text, {
              signal: searchAbortRef.current.signal,
            });
            if (reqId === requestSeqRef.current) {
              setSearchResults(searchRes.data.documents);
              setIsLoadingResults(false);
            }
          } catch (err) {
            if (err instanceof DOMException && err.name === "AbortError") {
              // Keep loading state; a newer request is in-flight
              return;
            }
            if (reqId === requestSeqRef.current) {
              console.error(
                "Error searching documents (debounced, text):",
                err
              );
              setSearchResults([]);
              setSearchResultsError(true);
              setIsLoadingResults(false);
            }
          }
        }
      } catch (e) {
        if (reqId === requestSeqRef.current) {
          console.error("Unexpected error in debounced search:", e);
          setSearchResults([]);
          setSearchResultsError(true);
          setIsLoadingResults(false);
          setAssistantResponseError(true);
          setIsLoadingResponse(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
      // Abort pending when input changes before debounce fires
      if (searchAbortRef.current) searchAbortRef.current.abort();
      if (summaryAbortRef.current) summaryAbortRef.current.abort();
    };
  }, [
    currentSearchText,
    hasAnyActiveFilter,
    publishedSince,
    createSearchParamsFromFilters,
    searchQuery,
  ]);
  // Trigger filtered search whenever filters change and at least one filter is active
  const prevHasAnyActiveFilter = useRef<boolean>(false);
  useEffect(() => {
    if (hasAnyActiveFilter) {
      // When filters are active, run a filtered search
      performFilteredSearch();
    } else if (prevHasAnyActiveFilter.current) {
      // Filters were just cleared: trigger a search with only searchText
      const text = latestSearchTextRef.current?.trim?.() || "";
      performSearch(text, true);
    }
    prevHasAnyActiveFilter.current = hasAnyActiveFilter;
  }, [hasAnyActiveFilter, performFilteredSearch, performSearch]);

  const getAssistantTitle = () => "Assistant";

  const handleDrawerSearch = useCallback(
    async (params: AdvancedSearchParams) => {
      try {
        setIsLoadingResults(true);
        setSearchResultsError(false);
        // Trigger summary in parallel (non-blocking)
        setIsLoadingResponse(true);
        setAssistantResponseError(false);

        const withText: AdvancedSearchParams & { searchText?: string } = {
          ...params,
          searchText:
            (typeof currentSearchText === "string"
              ? currentSearchText.trim()
              : "") || "",
        };

        // Fire summary request without blocking search results
        summaryService
          .getSummary(withText)
          .then((res) => {
            setAssistantResponse(res.answer);
          })
          .catch((err) => {
            console.error("Error getting assistant response (drawer):", err);
            setAssistantResponse(
              "Sorry, I couldn't get an AI response at this time. Please try again later."
            );
            setAssistantResponseError(true);
          })
          .finally(() => {
            setIsLoadingResponse(false);
          });

        // Await search results
        const response = await searchService.searchDocumentsAdvanced(withText);
        setSearchResults(response.data.documents);
      } catch (err) {
        console.error("Error performing drawer search:", err);
        setSearchResults([]);
        setSearchResultsError(true);
      } finally {
        setIsLoadingResults(false);
        onCloseFilterDrawer?.();
      }
    },
    [currentSearchText, onCloseFilterDrawer]
  );

  // Parse orgId (optional), documentId and language from search result ID
  // Supports both formats:
  // - Old: "<documentId>_<lang>"
  // - New: "<orgId>_<documentId>_<lang>"
  const parseDocumentId = (searchResultId: string) => {
    const lastUnderscoreIndex = searchResultId.lastIndexOf("_");
    if (lastUnderscoreIndex === -1) {
      // Fallback if no underscore found
      return { orgId: undefined, documentId: searchResultId, languageCode: "EN" };
    }

    const base = searchResultId.substring(0, lastUnderscoreIndex);
    const rawLang = searchResultId.substring(lastUnderscoreIndex + 1);
    const languageCode = (() => {
      const parts = rawLang.split("-");
      if (parts.length === 1) {
        // One part: uppercase whole code
        return rawLang.toUpperCase();
      }
      if (parts.length === 2) {
        // Two parts: language lower, second upper
        const language = (parts[0] || "").toLowerCase();
        const regionOrScript = (parts[1] || "").toUpperCase();
        return [language, regionOrScript].join("-");
      }
      if (parts.length === 3) {
        // Three parts: language lower, script TitleCase, region upper
        const language = (parts[0] || "").toLowerCase();
        const scriptRaw = parts[1] || "";
        const script = scriptRaw.charAt(0).toUpperCase() + scriptRaw.slice(1).toLowerCase();
        const region = (parts[2] || "").toUpperCase();
        return [language, script, region].join("-");
      }
      // More than three parts: leave unchanged
      return rawLang;
    })();

    const firstUnderscoreIndex = base.indexOf("_");
    if (firstUnderscoreIndex === -1) {
      // Old format: <documentId>_<lang>
      return { orgId: undefined, documentId: base, languageCode };
    }

    // New format: <orgId>_<documentId>_<lang>
    const orgId = base.substring(0, firstUnderscoreIndex);
    const documentId = base.substring(firstUnderscoreIndex + 1);
    return { orgId, documentId, languageCode };
  };

  // Handle document download from search results
  const handleDocumentDownload = async (result: SearchResult) => {
    try {
      if (downloadingIds.has(result.id)) return;
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.add(result.id);
        return next;
      });
      const { documentId, languageCode } = parseDocumentId(result.id);

      const downloadRequest: PolicyDownloadRequest = {
        agencyId: result.agencyId,
        platform_org_id: result.platform_org_id,
        policyDocument: {
          id: documentId,
          name: result.documentName,
        },
        latestVersion: {
          versionNumber: result.documentVersion || "1.0",
        },
        languageCode: languageCode,
      };

      await downloadPolicyDocumentComplete(downloadRequest);
    } catch (error) {
      console.error("Download failed:", error);
      // Error handling is done by the download service (shows modal for NO_PERMISSIONS)
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(result.id);
        return next;
      });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: {
          sm: 1.5, // Reduced gap for small screens
          md: 2, // Reduced gap for medium screens
          lg: 3, // Original gap for large+ screens
        },
        pb: 1,
      }}
    >
      {/* Main Content */}
      <Box sx={{ flex: 1, width: isXs || isSm || isMd ? "100%" : "auto" }}>
        {/* Back Navigation */}
        <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
          <Button
            onClick={onBack}
            sx={{
              color: "text.primary",
              p: 0,
              minWidth: "auto",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <ArrowLeftIcon size="xl" />
          </Button>
          <Typography
            component="h1"
            sx={{
              color: "text.primary",
              fontSize: getMainTitleFontSize(),
              fontWeight: "600",
              lineHeight: isXs
                ? "1.875rem"
                : isSm
                ? "2rem"
                : isMd
                ? "2.125rem"
                : "2.375rem",
              wordWrap: "break-word",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={onBack}
          >
            Search results
          </Typography>
          {/* Filter button now controlled from SearchAndFilterArea in search page header */}
        </Stack>

        {/* AI Assistant Response */}
        {!hideAssistantResponse && (
        <Paper
          elevation={0}
          sx={{
            py: 3, // Vertical padding stays the same
            px: {
              sm: 0, // Zero horizontal padding for small screens
              md: 2.5, // Slightly reduced for medium screens (20px)
              lg: 3, // Original padding for large+ screens (24px)
            },
            mb: 3,
          }}
        >
          <Box>
            {/* Logo and Title Row */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 0.5 }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <DiligentLogo width="22.9px" height="32px" />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "text.primary",
                  lineHeight: 1,
                }}
              >
                {getAssistantTitle()}
              </Typography>
            </Stack>

            {/* Content Row */}
            <Box sx={{ ml: "48px" }}>
              {isLoadingResponse ? (
                <>
                  <Skeleton
                    variant="text"
                    width="90%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="85%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="text" width="70%" height={20} />
                </>
              ) : assistantResponseError ? (
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: "error.main",
                    lineHeight: 1.5,
                  }}
                >
                  {assistantResponse}
                </Typography>
              ) : (
                <CollapsibleText maxHeight={150}>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      color: "text.primary",
                      lineHeight: 1.5,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {assistantResponse}
                  </Typography>
                </CollapsibleText>
              )}
            </Box>
          </Box>
        </Paper>
        )}

        {/* Search Results */}
        <Box>
          {isLoadingResults ? (
            <Box sx={{ py: 2 }}>
              {[...Array(5)].map((_, idx) => (
                <Paper
                  key={idx}
                  elevation={0}
                  sx={{
                    py: 3,
                    px: {
                      sm: 0,
                      md: 2.5,
                      lg: 3,
                    },
                    mb: 2,
                  }}
                >
                  <Stack direction="row" spacing={2}>
                    <Skeleton
                      variant="circular"
                      width={24}
                      height={24}
                      sx={{ mt: 0.5 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="40%" height={24} />
                      <Skeleton variant="text" width="60%" height={18} />
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={64}
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Box>
          ) : searchResultsError ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ color: "error.main", mb: 1 }}>
                Unable to load search results
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                There was an error searching documents. Please try again later.
              </Typography>
            </Box>
          ) : searchResults.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ color: "text.secondary", fontStyle: "italic" }}>
                No documents found for your search.
              </Typography>
            </Box>
          ) : (
            searchResults.map((result) => {
              const modifiedDate = new Date(result.modifiedDate);
              const daysAgo = Math.floor(
                (new Date().getTime() - modifiedDate.getTime()) /
                  (1000 * 3600 * 24)
              );

              const updatedText =
                daysAgo <= 0
                  ? "Updated today"
                  : daysAgo === 1
                  ? "Updated 1 day ago"
                  : `Updated ${daysAgo} days ago`;

              // Prepare all matching chunks for display
              const hasMatchingChunks =
                result.matching_chunks && result.matching_chunks.length > 0;

              return (
                <Paper
                  key={result.id}
                  elevation={0}
                  sx={{
                    py: hasMatchingChunks ? 3 : 1, // Vertical padding stays the same
                    px: {
                      sm: 0, // Zero horizontal padding for small screens
                      md: 2.5, // Slightly reduced for medium screens (20px)
                      lg: 3, // Original padding for large+ screens (24px)
                    },
                    mb: hasMatchingChunks ? 3 : 0,
                  }}
                >
                  <Stack direction="row" spacing={2}>
                    <DescriptionOutlinedIcon
                      sx={{
                        color: "text.secondary",
                        fontSize: "1.5rem",
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            fontSize: "1rem",
                            color: "text.primary",
                            textDecoration: "underline",
                            cursor: downloadingIds.has(result.id) ? "default" : "pointer",
                            pointerEvents: downloadingIds.has(result.id) ? "none" : "auto",
                            opacity: downloadingIds.has(result.id) ? 0.6 : 1,
                          }}
                          onClick={() => handleDocumentDownload(result)}
                        >
                          {result.documentName}.pdf
                        </Typography>
                        {downloadingIds.has(result.id) && (
                          <DownloadIndicator size={16} />
                        )}
                      </Stack>

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ mb: 2 }}
                      >
                        <Typography
                          sx={{
                            fontSize: {
                              sm: "0.65rem", // Smaller font for small screens
                              md: "0.75rem", // Original font for medium+ screens
                            },
                            color: "text.secondary",
                          }}
                        >
                          {updatedText}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: {
                              sm: "0.65rem", // Smaller font for small screens
                              md: "0.75rem", // Original font for medium+ screens
                            },
                            color: "text.secondary",
                          }}
                        >
                          {result.documentCategory &&
                            `• ${result.documentCategory}`}
                          {result.documentSubCategory &&
                            `• ${result.documentSubCategory}`}
                        </Typography>
                      </Stack>

                      {hasMatchingChunks && (
                        <CollapsibleText maxHeight={200}>
                          <Box>
                            {result.matching_chunks.map((chunk, index) => (
                              <Box
                                key={index}
                                sx={{
                                  mb:
                                    index < result.matching_chunks.length - 1
                                      ? 2
                                      : 0,
                                }}
                              >
                                {/* Page Numbers */}
                                <Typography
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "text.secondary",
                                    fontWeight: 500,
                                    mb: 0.5,
                                  }}
                                >
                                  Page{chunk.pageNumbers.length > 1 ? "s" : ""}:{" "}
                                  {chunk.pageNumbers.join(", ")}
                                </Typography>

                                {/* Chunk Content */}
                                <Typography
                                  sx={{
                                    fontSize: "0.875rem",
                                    color: "text.primary",
                                    lineHeight: 1.5,
                                    whiteSpace: "pre-line",
                                  }}
                                >
                                  {chunk.text}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </CollapsibleText>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              );
            })
          )}
        </Box>
      </Box>

      {/* Filter Panel - Hidden on small, medium, and large screens */}
      {!isXs && !isSm && !isMd && (
        <FilterPanel
          filterValues={_filterValues}
          onPolicyCodeChange={handlePolicyCodeChange}
          onLanguageChange={handleLanguageChange}
          onDocumentTypeChange={handleDocumentTypeChange}
          onDocumentCategoryChange={handleDocumentCategoryChange}
          onDocumentSubCategoryChange={handleDocumentSubCategoryChange}
          onPublishedSinceChange={handlePublishedSinceChange}
        />
      )}

      {(isXs || isSm || isMd) && (
        <SearchFilter
          open={!!filterDrawerOpen}
          onClose={onCloseFilterDrawer || (() => {})}
          onApply={handleDrawerSearch}
          filterValues={_filterValues}
          isLoading={_isLoadingFilterValues}
        />
      )}
    </Box>
  );
};

export default SearchResults;
